import React, { useMemo } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { ExpandMore } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import { getUserDataFromJwt } from "@_utils/helpers";
import { parseTextLines, noteLinesNeedExpand } from "../utils/parseTextLines";
import AreYouSurePopup from "@_components/popups/AreYouSurePopup/AreYouSurePopup";
import { useDispatch, useSelector } from "react-redux";
import { caseActivityActions } from "@_actions/caseActivity.actions";

import { useActivityExpand } from "../hooks/useActivityExpand";
import { useConfirmPopup } from "@_hooks/useConfirmPopup";
import CardShell from "../core/CardShell";
import styles from "../CaseActivityCard.module.css";

const NoteIcon = () => (
  <SvgIcon>
    <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1s-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7-.25c.41 0 .75.34.75.75s-.34.75-.75.75-.75-.34-.75-.75.34-.75.75-.75zM19 19H5V5h14v14z" />
    <path d="m15.08 11.03-2.12-2.12L7 14.86V17h2.1zm1.77-1.76c.2-.2.2-.51 0-.71l-1.41-1.41c-.2-.2-.51-.2-.71 0l-1.06 1.06 2.12 2.12 1.06-1.06z" />
  </SvgIcon>
);

// Max characters a single-line note can have before it needs an expand toggle
// (based on the 16px / 400px note line).
const NOTE_MAX_CHARS = 51;

/**
 * NoteActivityCard
 *
 * Renders CASEACTIVITY_TYPE_NOTE rows with expand/collapse and delete support.
 */
function NoteActivityCard({ activity: noteActivity }) {
  const dispatch = useDispatch();
  const jwtData = getUserDataFromJwt();
  const caseActivityReducer = useSelector((state) => state.caseActivityReducer);
  const { detailsOpen, animateExpand, toggle } = useActivityExpand();
  const confirm = useConfirmPopup();

  const canDelete = jwtData.username === noteActivity.user_name;

  const parsedLines = useMemo(
    () => parseTextLines(noteActivity.content?.text ?? ""),
    [noteActivity.content?.text],
  );

  // Ignore blank lines when deciding whether the note overflows.
  const canExpand = noteLinesNeedExpand(
    parsedLines.filter((line) => line.trim().length > 0),
    NOTE_MAX_CHARS,
  );

  const handleDelete = () => {
    confirm.prompt("Are you sure you want to delete this note?", () => {
      confirm.close();
      dispatch(
        caseActivityActions.deleteNote(noteActivity.case_id, noteActivity.id),
      );
    });
  };

  const contentPreview = detailsOpen ? (
    <div>
      {parsedLines.map((str, index) => (
        <div className={styles.note_line} key={index}>
          {str}
        </div>
      ))}
    </div>
  ) : (
    <div className={styles.note_collapsed_wrapper}>
      {parsedLines
        .map((str, index) => {
          if (index <= 1) {
            return (
              <div className={styles.note_line_truncated} key={index}>
                {str}
              </div>
            );
          }
          return null;
        })
        .filter(Boolean)}
    </div>
  );

  const openerColumn = (
    <>
      <div
        className={styles.note_delete_icon_wrapper}
        title={
          canDelete
            ? "Delete note"
            : "You can only delete notes created by yourself"
        }
      >
        <DeleteIcon
          data-testid="note-delete-icon"
          onClick={(e) => {
            e.stopPropagation();
            if (canDelete) handleDelete();
          }}
          className={
            canDelete
              ? styles.delete_shopping_icon
              : `${styles.delete_shopping_icon} ${styles.delete_icon_disabled}`
          }
        />
      </div>
      {canExpand && (
        <div
          className={styles.opener_expand_wrap}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
        >
          <ExpandMore
            className={`${styles.opener_expand_more} ${
              detailsOpen ? styles.gray : ""
            }`}
          />
        </div>
      )}
    </>
  );

  return (
    <>
      <CardShell
        activity={noteActivity}
        animateExpand={animateExpand}
        icon={<NoteIcon />}
        typeText="Note"
        typeSubText={noteActivity.user_name}
        contentPreview={contentPreview}
        openerColumn={openerColumn}
        onContentClick={canExpand ? toggle : undefined}
        hasOpener={canExpand}
      />

      <AreYouSurePopup
        open={confirm.open}
        close={confirm.close}
        onClick={confirm.action}
        isLoading={caseActivityReducer.loading}
      >
        <p className={styles.are_you_sure_preline}>{confirm.text}</p>
      </AreYouSurePopup>
    </>
  );
}

export default NoteActivityCard;
